import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Spinner from "../../../../components/Spinner";
import useDeleteDoc from "../../../../hooks/useDeleteDoc";
import { formatNumber } from "../../../../utils/format";
import Modal from "../../../../components/Modal";
import ModalContainer from "./ModalContainer";
import useUpdateDoc from "../../../../hooks/useUpdateDoc";
import { obtenerFechaActual } from "../../../../utils/actualDate";
import { useSnapshotWhereTwo } from "../../../../hooks/useSnapshotWhereTwo";

function Table({
  user,
  isFetchingUser,
  setPendingRequests,
  setApprovedRequest,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const { data, isFetching } = useSnapshotWhereTwo("requests", {
    filter: {
      firstCondition: "status",
      secondCondition: "==",
      thirdCondition: "pendiente",
    },
    second: {
      firstCondition: "majorCompanyId",
      secondCondition: "==",
      thirdCondition: user[0]?.majorCompanyId,
    },
  });

  const { data: dataApproved, isFetching: fetchingApproved } =
    useSnapshotWhereTwo("requests", {
      filter: {
        firstCondition: "status",
        secondCondition: "==",
        thirdCondition: "aprobado",
      },
      second: {
        firstCondition: "majorCompanyId",
        secondCondition: "==",
        thirdCondition: user[0]?.majorCompanyId,
      },
    });

  const { handleDelete } = useDeleteDoc();
  const [showModal, setShowModal] = useState(false);
  const [currentRequest, setCurrentRequest] = useState({});
  const [fetching, setFetching] = useState(false);
  const [status, setStatus] = useState("aprobado");
  const { handleUpdate } = useUpdateDoc();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    setPendingRequests(data.length);
    setApprovedRequest(dataApproved?.length);
  }, [isFetching, fetchingApproved]);

  const handleUpdateRequest = async (el) => {
    const data = { status: status };
    if (status === "aprobado") data.start_date = obtenerFechaActual();
    try {
      setFetching(true);
      handleUpdate("requests", el.id, data);
      setFetching(false);
    } catch (error) {
      console.log(error);
      setFetching(false);
    } finally {
      setFetching(false);
    }
  };

  const hDelete = async (id) => {
    try {
      const r = window.confirm("¿Estás seguro de eliminar esta solicitud?");
      if (!r) return;
      const res = await handleDelete("requests", id);
    } catch (error) {
      console.log(error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendiente: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        dot: "bg-yellow-400",
        label: "Pendiente",
      },
      aprobado: {
        bg: "bg-green-100",
        text: "text-green-800",
        dot: "bg-green-400",
        label: "Aprobado",
      },
      rechazado: {
        bg: "bg-red-100",
        text: "text-red-800",
        dot: "bg-red-400",
        label: "Rechazado",
      },
    };

    const config = statusConfig[status] || statusConfig.pendiente;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></div>
        {config.label}
      </span>
    );
  };

  if (isFetching || isFetchingUser) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <Spinner />
            <p className="text-gray-600">Cargando solicitudes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="text-center py-12 space-y-4">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              className="text-gray-400"
            >
              <path
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              No hay solicitudes
            </h3>
            <p className="text-gray-600">
              Aún no hay solicitudes de créditos pendientes
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal showModal={showModal} setShowModal={setShowModal}>
        <ModalContainer
          majorCompanyId={user[0]?.majorCompanyId}
          currentRequest={currentRequest}
          setCurrentRequest={setCurrentRequest}
        />
      </Modal>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Solicitante
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Plazo
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Celular
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.map((el, index) => (
                <tr
                  key={el.id}
                  className={`
                    transition-all duration-200 hover:bg-gray-50 group
                    ${
                      isLoaded
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-4"
                    }
                  `}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isLoaded
                      ? "fadeInUp 0.6s ease-out forwards"
                      : "none",
                  }}
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600 group-hover:bg-[#49b0a8] group-hover:text-white transition-colors duration-200">
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-900">{`${el.name} ${el.lastName}`}</div>
                      <div className="text-sm text-gray-500">
                        C.C: {el.identity}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {el.month} meses
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{el.cellphone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatNumber(el?.ammount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <select
                        onChange={(e) => setStatus(e.target.value)}
                        value={status}
                        className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                      >
                        <option value="aprobado">Aprobado</option>
                        <option value="rechazado">Rechazado</option>
                      </select>
                      <button
                        onClick={() => handleUpdateRequest(el)}
                        disabled={fetching}
                        className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        title="Actualizar estado"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setShowModal(true);
                          setCurrentRequest(el);
                        }}
                        className="text-[#49b0a8] hover:text-[#3a8a84] font-medium text-sm transition-colors duration-200"
                      >
                        Ver Solicitud
                      </button>
                      <Link
                        to={`/plataforma/simulador/${el.identity}/${el.id}`}
                      >
                        <button className="text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors duration-200">
                          Ver Crédito
                        </button>
                      </Link>
                      <button
                        onClick={() => hDelete(el.id)}
                        className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors duration-200"
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4 p-4">
          {data?.map((el, index) => (
            <div
              key={el.id}
              className={`
                bg-white border border-gray-200 rounded-xl p-4 space-y-4 hover:shadow-md transition-all duration-300
                ${
                  isLoaded
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }
              `}
              style={{
                animationDelay: `${index * 100}ms`,
                animation: isLoaded
                  ? "fadeInUp 0.6s ease-out forwards"
                  : "none",
              }}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h3 className="font-medium text-gray-900">{`${el.name} ${el.lastName}`}</h3>
                  <p className="text-sm text-gray-500">ID: {el.identity}</p>
                  <p className="text-sm text-gray-500">Tel: {el.cellphone}</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-900">
                    ${formatNumber(el?.ammount)}
                  </div>
                  {getStatusBadge("pendiente")}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setShowModal(true);
                    setCurrentRequest(el);
                  }}
                  className="flex-1 bg-[#49b0a8] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3a8a84] transition-colors duration-200"
                >
                  Ver Solicitud
                </button>
                <Link to={`/plataforma/simulador/${el.identity}/${el.id}`}>
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors duration-200">
                    Ver Crédito
                  </button>
                </Link>
                <button
                  onClick={() => hDelete(el.id)}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors duration-200"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}

export default Table;
